import { useFormik } from 'formik';
import React, { useState } from 'react';
import { FiMail } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { Button } from '@design-system/components/atoms/Button';
import { FormField } from '@design-system/components/molecules/FormField';
import { authService } from '@features/authentication/services/auth.service';
import { APP_ROUTES } from '@shared/constants/routes.constants';

const forgotPasswordSchema = Yup.object({
  email: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
});

export interface ForgotPasswordFormProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSuccess, onError }) => {
  const [serverError, setServerError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values) => {
      setServerError('');
      setIsLoading(true);

      try {
        await authService.generateOtp(values.email);

        // Generate a unique token to validate the navigation
        const otpSessionToken = crypto.randomUUID();
        sessionStorage.setItem('otp_session_token', otpSessionToken);
        sessionStorage.setItem('otp_session_email', values.email);

        onSuccess?.();
        // Redirect to verify code page with email and token in state
        void navigate(APP_ROUTES.VERIFY_CODE, {
          state: { email: values.email, otpSessionToken },
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Erro ao enviar código de recuperação. Tente novamente.';

        setServerError(errorMessage);
        onError?.(error instanceof Error ? error : new Error(errorMessage));
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleBack = () => {
    void navigate(APP_ROUTES.LOGIN);
  };

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
      <FormField
        name="email"
        label="Email"
        type="email"
        placeholder="seu@email.com"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.email}
        touched={formik.touched.email}
        required
        fullWidth
        autoComplete="email"
        icon={<FiMail className="w-5 h-5" />}
      />

      {serverError && (
        <div
          className="p-3 rounded-lg bg-danger-50 border border-danger-200 text-danger-700 text-sm"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={isLoading || !formik.isValid}
      >
        {isLoading ? 'Enviando...' : 'Enviar código de recuperação'}
      </Button>

      <Button
        type="button"
        variant="outline"
        size="lg"
        fullWidth
        onClick={handleBack}
        disabled={isLoading}
      >
        Voltar
      </Button>
    </form>
  );
};
